import { inject, parse } from "regexparam";
import { get, writable, type Readable, type Writable } from "svelte/store";
import { normalizeRelative, trimLeft } from "./path";
import { router } from "./router";

export interface RouteNode {
  currentPath: Readable<string>;
  matches: (children: any[]) => void;
  hasMatch: () => boolean;
  generatePath: (path: string) => string;
}

export class Route implements RouteNode {
  readonly path: string;
  readonly matcher: { keys: string[]; pattern: RegExp };
  readonly store: Writable<{ matches: boolean; params: { [key: string]: string } }>;
  readonly parent: RouteNode;
  breadcrumb: any;
  matchedChildren: Array<Route>;
  currentPath: Writable<string>;

  constructor(config: { path: string; parent: RouteNode; breadcrumb: any }) {
    this.path = normalizeRelative(config.path);
    this.matcher = this.path.endsWith("/*")
      ? parse(this.path.substring(0, this.path.length - 2), true)
      : parse(this.path);
    this.breadcrumb = config.breadcrumb;
    this.store = writable({ matches: false, params: {} });
    this.parent = config.parent || router;
    this.matchedChildren = [];
    this.currentPath = writable("");
  }

  async test(path) {
    this.matchedChildren = [];

    // test if it matches as long as parent has not already matched
    let matches = this.parent.hasMatch() ? false : this.matcher.pattern.test(path);

    // extract route params if match
    let params = matches ? groups(path, this.matcher) : {};

    // check if store needs update
    const current = get(this.store);
    if (current.matches !== matches || !isSameObject(current.params, params)) {
      this.store.set({ matches, params });
    }

    // inform parent for match
    if (matches && !this.parent.hasMatch()) {
      this.parent.matches([this]);

      // inform children of path change
      this.currentPath.set(trimLeft(inject(this.path, params), path));
    }
  }

  generatePath(p: string) {
    let params = get(this.store).params;
    return this.parent.generatePath(inject(this.path, params) + normalizeRelative(p));
  }

  matches(children: any[]) {
    this.matchedChildren = children;
    this.parent.matches([this, ...children]);
  }

  hasMatch() {
    return this.matchedChildren.length > 0;
  }

  subscribe() {
    // subscribe to parent path changes
    return this.parent.currentPath.subscribe((path) => this.test(path));
  }
}

function groups(path, regex) {
  let i = 0,
    out = {};
  let matches = regex.pattern.exec(path);
  while (i < regex.keys.length) {
    out[regex.keys[i]] = matches[++i] || null;
  }
  return out;
}

function isSameObject(obj1: object, obj2: object) {
  return (
    JSON.stringify(obj1, Object.keys(obj1).sort()) ===
    JSON.stringify(obj2, Object.keys(obj2).sort())
  );
}
